import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { AppLocale } from '../i18n/i18n';

const languages: Array<{
  locale: AppLocale;
  code: string;
  name: string;
  icon: string;
}> = [
  { locale: 'hr-HR', code: 'HR', name: 'Hrvatski', icon: '/flags/hr.png' },
  { locale: 'en-US', code: 'EN', name: 'English', icon: '/flags/en.svg' },
  { locale: 'de-DE', code: 'DE', name: 'Deutsch', icon: '/flags/de.svg' }
];

interface LanguageSwitcherProps {
  value: AppLocale;
  onChange: (locale: AppLocale) => void | Promise<void>;
  ariaLabel: string;
  variant?: 'compact' | 'login' | 'settings';
}

export function LanguageSwitcher({
  value,
  onChange,
  ariaLabel,
  variant = 'compact'
}: LanguageSwitcherProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selected = languages.find((language) => language.locale === value) ?? languages[0];

  useEffect(() => {
    function closeOnOutside(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', closeOnOutside);
    return () => document.removeEventListener('pointerdown', closeOnOutside);
  }, []);

  return (
    <div className={`language-picker language-picker--${variant} ${open ? 'is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="language-picker__trigger"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setOpen(false);
        }}
      >
        <img src={selected.icon} alt="" />
        <span>{variant === 'settings' ? selected.name : selected.code}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="language-picker__menu" role="listbox" aria-label={ariaLabel}>
          {languages.map((language) => (
            <button
              type="button"
              role="option"
              aria-selected={language.locale === value}
              key={language.locale}
              onClick={() => {
                setOpen(false);
                void onChange(language.locale);
              }}
            >
              <img src={language.icon} alt="" />
              <span><strong>{language.name}</strong><small>{language.code}</small></span>
              {language.locale === value && <Check />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
